import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";

// Lazy-load UI providers that pull in heavy deps (sonner, radix-toast, floating-ui).
// They are not required for first paint and shave ~28 KiB off the initial bundle.
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const TooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then((m) => ({ default: m.TooltipProvider })),
);

// Main traffic page: eager import (no lazy boundary) to remove the render-delay
// caused by waiting for the page chunk after react-vendor loads. This is the LCP page.
import CapturaAmplificador01A from "./pages/CapturaAmplificador01A.tsx";

// Other capture pages: kick off chunk download IMMEDIATELY in parallel.
const capturaAmplificador02APromise = import("./pages/CapturaAmplificador02A.tsx");
const capturaOrganica01APromise = import("./pages/CapturaOrganica01A.tsx");
const capturaAmplificador01BPromise = import("./pages/CapturaAmplificador01B.tsx");
const capturaAmplificador02BPromise = import("./pages/CapturaAmplificador02B.tsx");

const CapturaAmplificador02A = lazy(() => capturaAmplificador02APromise);
const CapturaOrganica01A = lazy(() => capturaOrganica01APromise);
const CapturaAmplificador01B = lazy(() => capturaAmplificador01BPromise);
const CapturaAmplificador02B = lazy(() => capturaAmplificador02BPromise);
const Index = lazy(() => import("./pages/Index.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const ObrigadoTrafego = lazy(() => import("./pages/ObrigadoTrafego.tsx"));
const ObrigadoOrganico = lazy(() => import("./pages/ObrigadoOrganico.tsx"));
const ObrigadoAPI = lazy(() => import("./pages/ObrigadoAPI.tsx"));
const ObrigadoAluno = lazy(() => import("./pages/ObrigadoAluno.tsx"));
const ListaDeReserva = lazy(() => import("./pages/ListaDeReserva.tsx"));
const FuraFila = lazy(() => import("./pages/FuraFila.tsx"));
const PaginaDeMaterial = lazy(() => import("./pages/PaginaDeMaterial.tsx"));
const Pitch = lazy(() => import("./pages/Pitch.tsx"));
const Dash = lazy(() => import("./pages/Dash.tsx"));
const Sorteio = lazy(() => import("./pages/Sorteio.tsx"));
const Sorteador = lazy(() => import("./pages/Sorteador.tsx"));
const ListaDeReservaImersao = lazy(() => import("./pages/ListaDeReservaImersao.tsx"));
const FuraFilaImersao = lazy(() => import("./pages/FuraFilaImersao.tsx"));
const PitchImersao = lazy(() => import("./pages/PitchImersao.tsx"));
const FunilFormularioObrigado = lazy(() => import("./pages/FunilFormularioObrigado.tsx"));
const Apostila = lazy(() => import("./pages/Apostila.tsx"));
const Boleto = lazy(() => import("./pages/Boleto.tsx"));
const Condicao = lazy(() => import("./pages/Condicao.tsx"));


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0A0A0B" }} />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/Captura-01-A" element={<CapturaAmplificador01A />} />
              <Route path="/funil-formulario" element={<CapturaAmplificador01A />} />
              <Route path="/funil-formulario-obrigado" element={<FunilFormularioObrigado />} />
              <Route path="/Captura-01-B" element={<CapturaAmplificador01B />} />
              <Route path="/Captura-02-A" element={<CapturaAmplificador02A />} />
              <Route path="/Captura-02-B" element={<CapturaAmplificador02B />} />
              <Route path="/Captura-Organica-01-A" element={<CapturaOrganica01A />} />
              <Route path="/Obrigado-Trafego" element={<ObrigadoTrafego />} />
              <Route path="/Obrigado-Organico" element={<ObrigadoOrganico />} />
              <Route path="/Obrigado-API" element={<ObrigadoAPI />} />
              <Route path="/obrigado-aluno" element={<ObrigadoAluno />} />
              <Route path="/lista-de-reserva" element={<ListaDeReserva />} />
              <Route path="/fura-fila" element={<FuraFila />} />
              <Route path="/pagina-de-material" element={<PaginaDeMaterial />} />
              <Route path="/pitch" element={<Pitch />} />
              <Route path="/dash" element={<Dash />} />
              <Route path="/sorteio" element={<Sorteio />} />
              <Route path="/sorteador" element={<Sorteador />} />
              <Route path="/lista-de-reserva-imersao" element={<ListaDeReservaImersao />} />
              <Route path="/fura-fila-imersao" element={<FuraFilaImersao />} />
              <Route path="/pitch-imersao" element={<PitchImersao />} />
              <Route path="/apostila" element={<Apostila />} />
              <Route path="/boleto" element={<Boleto />} />
              <Route path="/condicao" element={<Condicao />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </Suspense>
  </QueryClientProvider>
);

export default App;
